import Draggable from 'react-draggable';
import { useToggle } from 'react-use';
import classNames from 'classnames';

import FeedbackReview from 'modals/FeedbackReview';
import PlatformReview from 'modals/PlatformReview';
import ProviderReview from 'modals/ProviderReview';
import ActiveParticipant from 'widgets/zoom/ActiveParticipant';
import Captions from 'widgets/zoom/Captions';
import ChatSidebar from 'widgets/zoom/ChatSidebar';
import Controls from 'widgets/zoom/Controls';
import Header from 'widgets/zoom/Header';
import Participants from 'widgets/zoom/Participants';

import { useZoomCall } from 'hooks';
import { useWidth } from 'hooks/useWidth';
import { ReviewOrigin } from 'utils/enums';

import { useRoomHook } from './room.hook';

const Room = () => {
  const { isMobile } = useWidth();
  const { isMinimize, isOpenChat, loadingRoom, room } = useZoomCall();
  const [isFullscreen, toggleIsFullscreen] = useToggle(false);

  const {
    activeParticipant,
    activeSpeakers,
    connectionLost,
    handleCloseFeedbackReview,
    handleLogout,
    handleSkipPlatformReview,
    handleSkipProviderReview,
    handleSubmitPlatformReview,
    handleSubmitProviderReview,
    interpreterRequested,
    isOpenFeedbackReview,
    isOpenPlatformReview,
    isOpenProviderReview,
    participantDisconnected,
    participants,
    providerForReview,
    refRoom
  } = useRoomHook();

  const filteredParticipants = participants.filter((el) => el.userId !== activeParticipant?.userId);
  const isRoom = room && !(isOpenProviderReview || isOpenPlatformReview || isOpenFeedbackReview);

  const zoomCallRoomContentClassName = classNames(
    'overflow-hidden relative z-10 h-full shadow-2xl aspect-video w-full',
    {
      '!absolute !z-30 pointer-events-none left-5 top-2.5 z-20 !h-[56px] max-w-[76px] !shadow rounded-xl':
        isMobile && isOpenChat,
      '!w-[205px] md:!h-fit rounded-2xl': isMinimize,
      'md:max-w-[900px] md:max-h-[506px] m-auto md:rounded-2xl': !isFullscreen
    }
  );

  return (
    <>
      <ProviderReview
        isOpen={isOpenProviderReview}
        provider={providerForReview}
        onClose={handleSubmitProviderReview}
        onSkip={handleSkipProviderReview}
      />
      <PlatformReview
        isOpen={isOpenPlatformReview}
        origin={ReviewOrigin.Appointment}
        onClose={handleSubmitPlatformReview}
        onSkip={handleSkipPlatformReview}
      />
      <FeedbackReview
        isOpen={isOpenFeedbackReview}
        origin={ReviewOrigin.Appointment}
        onClose={handleCloseFeedbackReview}
      />
      {isRoom && (
        <Draggable
          allowAnyClick={true}
          axis={isMinimize ? 'both' : 'none'}
          bounds="parent"
          disabled={isMobile && isOpenChat}
          nodeRef={refRoom as React.RefObject<HTMLElement>}
        >
          <div
            className={classNames('fixed z-40 flex', {
              'bottom-5 left-5': isMinimize,
              hidden: loadingRoom,
              'left-0 top-0 h-full w-full bg-black/70': !isMinimize
            })}
            ref={refRoom}
          >
            <Header />
            <div className={zoomCallRoomContentClassName}>
              <ActiveParticipant
                participant={activeParticipant}
                activeSpeakers={activeSpeakers}
                participantDisconnected={participantDisconnected}
                connectionLost={connectionLost}
                interpreterRequested={interpreterRequested}
              />
              <div className={isMinimize ? '' : 'relative'}>
                <Participants participants={filteredParticipants} activeSpeakers={activeSpeakers} />
                <Captions />
                <Controls
                  connectionLost={connectionLost}
                  handleLogout={handleLogout}
                  toggleIsFullscreen={toggleIsFullscreen}
                  isFullscreen={isFullscreen}
                />
              </div>
            </div>
            <ChatSidebar />
          </div>
        </Draggable>
      )}
    </>
  );
};

export default Room;
